import { z } from 'zod';
import { DomainError } from '../errors.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import { canonicalDec, decCmp, decDiv, decMul, formatDec, isDecimalText } from './decimal.js';

/**
 * Yakıt tankı (ADR 0117 §1; ortak belgesi §6 "Tanklar ve yakıt türleri ekranı", ekran 02). Tank
 * gemi verisidir: gemi sunucusu yazar, kurulum devrinde ofis hazırlar. Sayılar kanonik ondalık
 * metindir (ADR 0117 §11); negatif değer yasaktır. Doluluk yalnız hacimden hesaplanır: kütle
 * (MT) m³ kapasiteye bölünmez (ortak belgesi §6 "Hesap ve doğrulama"). Saf modül.
 */

/** Tank görevi: storage / settling / service / overflow / diğer. */
export const TANK_ROLES = ['storage', 'settling', 'service', 'overflow', 'other'] as const;
export type TankRole = (typeof TANK_ROLES)[number];

/** Konum: iskele / sancak / merkez. */
export const TANK_LOCATIONS = ['port', 'starboard', 'centre'] as const;
export type TankLocation = (typeof TANK_LOCATIONS)[number];

/** Ölçüm altyapısı: sounding / ullage / sensör. */
export const TANK_GAUGING = ['sounding', 'ullage', 'sensor'] as const;
export type TankGauging = (typeof TANK_GAUGING)[number];

/** Seviye eşiklerinin birimi açıkça % ya da m³ (ortak belgesi §6). */
export const TANK_LEVEL_UNITS = ['pct', 'm3'] as const;
export type TankLevelUnit = (typeof TANK_LEVEL_UNITS)[number];

/** Tankın tablo dosyaları: kalibrasyon (sounding/ullage → hacim) ve trim/list düzeltmesi. */
export const TANK_TABLE_KINDS = ['calibration', 'trim_list'] as const;
export type TankTableKind = (typeof TANK_TABLE_KINDS)[number];

/** Tank kodu: büyük harf, rakam ve `.`, `_`, `/`, `-`; gemide tekil. */
export const TANK_CODE_PATTERN = /^[A-Z0-9][A-Z0-9._/-]{0,29}$/;

/** Tankın bağlı tüketici ve izinli yakıt üst sınırları. */
export const TANK_MAX_FUELS = 20;
export const TANK_MAX_CONSUMERS = 50;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const requiredText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));

/** Negatif olmayan kanonik ondalık metin; JS sayısı kabul edilmez (kayan nokta yok). */
const amount = z
  .string(typed('INVALID_DECIMAL'))
  .refine(isDecimalText, { message: 'INVALID_DECIMAL', abort: true })
  .transform(canonicalDec)
  .refine((value) => !value.startsWith('-'), 'NEGATIVE_NOT_ALLOWED');
const optionalAmount = amount.nullish().transform((value) => value ?? null);

const uniqueIds = (max: number) =>
  z
    .array(z.uuid('INVALID_ID'), typed('INVALID_LIST'))
    .max(max, 'TOO_LONG')
    .refine((list) => new Set(list).size === list.length, 'DUPLICATE_VALUE');

export const fuelTankSchema = z
  .strictObject({
    code: z
      .string(typed('INVALID_TEXT'))
      .transform((value) => value.trim())
      .pipe(z.string().min(1, 'REQUIRED').regex(TANK_CODE_PATTERN, 'INVALID_CODE')),
    name: requiredText(80),
    role: z.enum(TANK_ROLES, typed('INVALID_OPTION')),
    location: z.enum(TANK_LOCATIONS, typed('INVALID_OPTION')),
    allowedFuelTypeIds: uniqueIds(TANK_MAX_FUELS).refine((list) => list.length > 0, 'REQUIRED'),
    consumerIds: uniqueIds(TANK_MAX_CONSUMERS)
      .nullish()
      .transform((value) => value ?? []),
    description: z
      .string(typed('INVALID_TEXT'))
      .transform((value) => value.normalize('NFC').trim())
      .pipe(z.string().max(500, 'TOO_LONG'))
      .transform((value) => (value === '' ? null : value))
      .nullish()
      .transform((value) => value ?? null),
    capacityM3: amount.refine((value) => value !== '0', 'OUT_OF_RANGE'),
    fillLimitPct: amount.refine(
      (value) => value !== '0' && decCmp(value, '100') <= 0,
      'OUT_OF_RANGE',
    ),
    unusableM3: amount.nullish().transform((value) => value ?? '0'),
    lowLevel: optionalAmount,
    highLevel: optionalAmount,
    levelUnit: z
      .enum(TANK_LEVEL_UNITS, typed('INVALID_OPTION'))
      .nullish()
      .transform((value) => value ?? 'pct'),
    measurementMethod: z.enum(TANK_GAUGING, typed('INVALID_OPTION')),
    active: z
      .boolean(typed('INVALID_BOOLEAN'))
      .nullish()
      .transform((value) => value ?? true),
  })
  .superRefine((tank, ctx) => {
    // Alan kuralı geçmemiş kapasiteyle karşılaştırma yapılmaz (hata kendi alanında)
    const capacityOk = !tank.capacityM3.startsWith('-') && tank.capacityM3 !== '0';
    if (!capacityOk) return;
    if (decCmp(tank.unusableM3, tank.capacityM3) >= 0) {
      ctx.addIssue({ code: 'custom', path: ['unusableM3'], message: 'UNUSABLE_EXCEEDS_CAPACITY' });
    }
    // Eşik % ise en çok 100, m³ ise en çok kapasite
    const ceiling = tank.levelUnit === 'pct' ? '100' : tank.capacityM3;
    for (const key of ['lowLevel', 'highLevel'] as const) {
      const value = tank[key];
      if (value !== null && decCmp(value, ceiling) > 0) {
        ctx.addIssue({ code: 'custom', path: [key], message: 'OUT_OF_RANGE' });
      }
    }
    if (
      tank.lowLevel !== null &&
      tank.highLevel !== null &&
      decCmp(tank.lowLevel, tank.highLevel) >= 0
    ) {
      ctx.addIssue({ code: 'custom', path: ['highLevel'], message: 'LEVEL_ORDER' });
    }
  });
export type FuelTankInput = z.output<typeof fuelTankSchema>;

export function parseFuelTank(input: unknown): ParseResult<FuelTankInput> {
  const result = fuelTankSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : {
        ok: false,
        // Liste öğesinin sorunu alanın sorunudur (`allowedFuelTypeIds.0` → `allowedFuelTypeIds`)
        issues: toIssues(result.error)
          .map((issue) => ({ ...issue, path: issue.path.split('.')[0] ?? issue.path }))
          .filter(
            (issue, index, all) =>
              all.findIndex((i) => i.path === issue.path && i.code === issue.code) === index,
          ),
      };
}

/**
 * Tablo dosyasının sayısal satırı (ADR 0117 §4; isteğe bağlı): seviye (sounding ya da ullage, cm)
 * → hacim (m³); trim/list tablosunda düzeltme ayrıca. Y4'te ölçüm hesabı okur.
 */
export const tankTableRowSchema = z.strictObject({
  level: amount,
  volumeM3: amount,
  trim: z
    .string()
    .refine(isDecimalText, 'INVALID_DECIMAL')
    .transform(canonicalDec)
    .nullish()
    .transform((value) => value ?? null),
  list: z
    .string()
    .refine(isDecimalText, 'INVALID_DECIMAL')
    .transform(canonicalDec)
    .nullish()
    .transform((value) => value ?? null),
});
export type TankTableRow = z.output<typeof tankTableRowSchema>;
export const TANK_TABLE_MAX_ROWS = 5000;

/**
 * Doluluk yüzdesi = hacim / kapasite × 100 (ortak belgesi §6). Yalnız m³ alır: kütle (MT) m³
 * kapasiteye bölünmez; kütle önce aynı referans koşulunda hacme çevrilir (ölçüm, Y4).
 */
export function fillPct(input: { volumeM3: string; capacityM3: string }): string {
  return formatDec(decDiv(decMul(input.volumeM3, '100', 12), input.capacityM3, 2));
}

/** İşletme kapasitesi (m³) = kapasite × dolum sınırı / 100. */
export function operatingCapacityM3(input: { capacityM3: string; fillLimitPct: string }): string {
  return formatDec(decDiv(decMul(input.capacityM3, input.fillLimitPct, 12), '100', 6));
}

/**
 * Hareketi olan tank silinemez (ortak belgesi §6 "İşlemler"): kullanım dışı bırakılır (`active`),
 * geçmişi korunur. Hareket sayısını yazma yolu verir (Y2'de ROB defteri).
 */
export function assertTankRemovable(input: { movements: number }): void {
  if (input.movements > 0) throw new DomainError('TANK_HAS_MOVEMENTS');
}
